// tslint:disable:no-console
import 'core-js/es/array';
import * as React from 'react';
import { Archiver } from '../services/Archiver';
import { CheckStatus, ErrorStatusReasons, checkValidCYO } from '../services/CheckHashable';
import { hashFile } from '../services/Hashing';
import { getFileContentsFromZip, getFileListFromZip } from '../services/LoadZipFile';
import { readFileAsText } from '../services/ReadFile';
import { Action } from './Action';
import { FileList } from './FileList';
import { Preview } from './Preview';
import './Uploader.css';
import { UploadForm } from './UploadForm';

export interface UploaderProps {
  uploadUrl: string;
  onUploaded: () => void;
};
type FileCheckStatus = 'pending'|CheckStatus;
interface UploaderState {
  errorReasons: string[];
  files: File[];
  fileChecks: Array<FileCheckStatus>;
  nowChecking: boolean;
  nowHashing: boolean;
  nowUploading: boolean;
  notice: string;
  previewTexts: Array<string|null>;
  currentIndex: number;
  uploadFiles: UploadFile[];
};
interface UploadFile {
  name: string;
  source: 'zip'|'file';
  sourceName: string;
  index: number;
};

export class Uploader extends React.Component<UploaderProps, UploaderState> {
  private archiver!: Archiver;

  constructor(props: UploaderProps) {
    super(props);
    this.executeHash = this.executeHash.bind(this);
    this.executeUpload = this.executeUpload.bind(this);
    this.fileListHandler = this.fileListHandler.bind(this);
    this.receiveCurrentIndex = this.receiveCurrentIndex.bind(this);
    this.state = {
      currentIndex: 0,
      errorReasons: [],
      fileChecks: [],
      files: [],
      notice: '',
      nowChecking: false,
      nowHashing: false,
      nowUploading: false,
      previewTexts: [],
      uploadFiles: [],
    };
  }

  public render() {
    return (
      <div className={'Container'}>
        <div className={'Notice'}><span hidden={!this.state.notice}>{this.state.notice}</span></div>
        <UploadForm fileListHandler={this.fileListHandler}/>
        <Action nowChecking={this.state.nowChecking}
                nowHashing={this.state.nowHashing}
                nowUploading={this.state.nowUploading}
                hashable={this.hashable} executeHash={this.executeHash}
                uploadable={this.uploadable} executeUpload={this.executeUpload}/>
        <FileList files={this.receiptFiles}
                  currentIndex={this.state.currentIndex}
                  onClick={this.receiveCurrentIndex}/>
        <Preview previewText={this.previewText}/>
      </div>
    );
  }

  public async fileListHandler(files: File[]) {
    this.setState({ nowChecking: true });
    await this.checkFiles(files, await this.getTargetFiles(files));
    this.setState({ nowChecking: false });
  }

  public async executeHash() {
    const previewTexts = this.state.previewTexts;

    this.setState({ nowHashing: true });
    for (const [previewText, idx] of previewTexts.map((t, i) => [t, i] as [string, number])) {
      if (!previewText) {
        const uploadFile = this.state.uploadFiles[idx];
        const hashedContent = hashFile(await this.readFile(uploadFile));
        this.archiver.addContent(uploadFile.name, hashedContent);
        previewTexts[idx] = this.get10Lines(hashedContent);
        this.setState({ previewTexts: Array.from(previewTexts) });
      }
    }
    this.setState({ nowHashing: false });
  }

  public async executeUpload() {
    this.setState({ nowUploading: true });
    const blob = await this.archiver.blob;
    fetch(this.props.uploadUrl, {
      body: blob,
      headers: { "Content-Type": "application/zip" },
      method: 'PUT',
      mode: 'cors'
    }).then(res => {
      this.props.onUploaded();
      console.log('upload is success') // tslint:disable-line
    }).catch(err => {
      this.setState({
        notice: `アップロードに失敗しました: ${err}`,
        nowUploading: false,
      });
      console.log(err); // tslint:disable-line
    });
  }

  public receiveCurrentIndex(currentIndex: number) {
    this.setState({ currentIndex });
  }

  private async checkFiles(files: File[], uploadFiles: UploadFile[]) {
    const [fileChecks, previewTexts, errorReasons] = await this.initFileState(files, uploadFiles);

    for (const uploadFile of uploadFiles) {
      const {name: filename, index: idx} = uploadFile;
      const fileContent = await this.readFile(uploadFile);
      const { status, errorLineNo, errorColumnNo, errorVal, isHashed } = checkValidCYO(fileContent);
      if (status === "ok" && isHashed) {
        await this.archiver.addContent(filename, fileContent);
        previewTexts[idx] = this.get10Lines(fileContent);
        this.setState({ previewTexts: Array.from(previewTexts) });
      };
      fileChecks[idx] = status;
      if (status !== "ok") {
        errorReasons[idx] = [
          ErrorStatusReasons[status],
          errorLineNo ? ` (行番号: ${errorLineNo})` : "",
          errorColumnNo ? ` (列番号: ${errorColumnNo})` : "",
          errorVal ? ` (値: ${errorVal})` : "",
        ].join('')
      }
      this.setState({ fileChecks: Array.from(fileChecks) });
    }
  }

  private async getTargetFiles(files: File[]): Promise<UploadFile[]> {
    // matching for application/zip, application/x-zip, application/x-zip-compressed
    const regexZip = /^application\/(?:x-)?zip(?:-compressed)?$/;
    if (files.length === 1 && files[0].type.match(regexZip)) {
      const zipFile = files[0];
      return getFileListFromZip(zipFile)
              .then(filenames => (
                filenames.map((name, index) => {
                  return { name, index, source: "zip" } as UploadFile;
                })
              ));
    } else {
      return Promise.resolve(files.map((file, index) => {
        return { name: file.name, index, source: "file" } as UploadFile
      }));
    }
  }

  private async initFileState(files: File[], uploadFiles: UploadFile[]): Promise<[Array<FileCheckStatus>, string[], string[]]> {
    const fileChecks = new Array(uploadFiles.length).fill('pending');
    const previewTexts = new Array(uploadFiles.length).fill('');
    const errorReasons = new Array(uploadFiles.length).fill('');
    const currentIndex = 0;

    this.archiver = new Archiver();
    this.setState({ files, uploadFiles, fileChecks, previewTexts, errorReasons, currentIndex });

    return [fileChecks, previewTexts, errorReasons];
  }

  private get previewText() {
    return this.state.previewTexts[this.state.currentIndex] || '';
  }

  private get validAll() {
    if (this.state.uploadFiles.length) {
      return this.state.fileChecks.every(status => status === 'ok');
    } else {
      return false;
    }
  }

  private get hashedAll() {
    if (this.state.uploadFiles.length) {
      return this.state.previewTexts.every(s => !!s);
    } else {
      return false;
    }
  }

  private get hashable() {
    return this.validAll && !this.hashedAll && !this.state.nowHashing;
  }

  private get uploadable() {
    return this.validAll && this.hashedAll && !this.state.nowChecking;
  }

  private get receiptFiles() {
    if (this.state.uploadFiles === null) {
      return [];
    } else {
      return this.state.uploadFiles.map(({name, index}) => {
        return {
          isHashed: !!this.state.previewTexts[index],
          name,
          status: this.state.fileChecks[index],
          errorReason: this.state.errorReasons[index],
        };
      });
    }
  }

  private get10Lines(fileContents: string): string {
    const sep = "\r\n";
    return fileContents.split(sep, 11).slice(0, 10).join(sep);
  }

  private async readFile(file: UploadFile): Promise<string> {
    if (file.source === "zip") {
      return await getFileContentsFromZip(this.state.files[0], file.name);
    } else {
      return await readFileAsText(this.state.files[file.index]);
    }
  }
}
